import { type ChangeEvent, useState } from 'react';

import { Textarea } from '~/components/form';
import {
  Alert,
  Button,
  Modal,
  ModalActions,
  PanelTable,
  type PanelTableColumn,
} from '~/components/ui';
import { isCanonicalJsonSchema, jsonSchemaToSchemaFields } from '~/lib/ai/jsonSchema';
import type { SchemaField } from '~/lib/ai/schema';
import type { FieldCatalogEntry } from '~/modules/query/ai/types';

export interface SchemaImportModalProps {
  catalog: FieldCatalogEntry[];
  currentFieldCount: number;
  onClose: () => void;
  onImport: (fields: SchemaField[]) => void;
}

interface PreviewResult {
  fields: SchemaField[];
  unmappableKeys: string[];
}

const TYPE_LABELS: Record<SchemaField['type'], string> = {
  array: 'Array',
  boolean: 'Boolean',
  number: 'Number',
  object: 'Object',
  string: 'String',
};

/**
 * Imports a JSON schema document into the builder. Parsing only previews; the
 * builder changes when Import fields is confirmed, so Cancel always leaves the
 * current rows untouched.
 */
export default function SchemaImportModal({
  catalog,
  currentFieldCount,
  onClose,
  onImport,
}: SchemaImportModalProps) {
  const [step, setStep] = useState<'input' | 'preview'>('input');
  const [rawText, setRawText] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  const labelByKey = new Map(catalog.map((entry) => [entry.key, entry.label]));

  const readFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    try {
      setRawText(await file.text());
      setInputError(null);
    } catch {
      setInputError('The selected file could not be read. Choose it again.');
    }
  };

  const parse = () => {
    const text = rawText.trim();
    if (!text) {
      setInputError('Paste a JSON schema or choose a file to import.');
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setInputError('This is not valid JSON. Check the syntax and try again.');
      return;
    }

    if (!isCanonicalJsonSchema(parsed)) {
      setInputError(
        'This JSON is not a recognized JSON schema. It needs "type": "object" and a "properties" object.',
      );
      return;
    }

    setInputError(null);
    setPreview(jsonSchemaToSchemaFields(parsed, catalog));
    setStep('preview');
  };

  const columns: PanelTableColumn<SchemaField>[] = [
    {
      header: 'Business field',
      render: (field) => labelByKey.get(field.propertyName) ?? field.propertyName,
    },
    { header: 'Type', width: 'w-28', render: (field) => TYPE_LABELS[field.type] },
    { header: 'Required', width: 'w-28', render: (field) => (field.required ? 'Yes' : 'No') },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Import JSON schema"
      description={
        step === 'input'
          ? 'Paste a JSON schema document or choose a .json file, then preview what it adds.'
          : 'Review what will replace the current rows before importing.'
      }
      testId="ai-digital-assistants-schema-import-modal"
      footer={
        step === 'input' ? (
          <ModalActions
            onDismiss={onClose}
            dismissLabel="Cancel"
            dismissTestId="ai-digital-assistants-schema-import-cancel"
          >
            <Button onClick={parse} testId="ai-digital-assistants-schema-import-parse">
              Parse and preview
            </Button>
          </ModalActions>
        ) : (
          <ModalActions
            onDismiss={() => setStep('input')}
            dismissLabel="Back"
            dismissTestId="ai-digital-assistants-schema-import-back"
          >
            <Button
              onClick={() => preview && onImport(preview.fields)}
              testId="ai-digital-assistants-schema-import-confirm"
            >
              Import fields
            </Button>
          </ModalActions>
        )
      }
    >
      {step === 'input' ? (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="digital-assistant-schema-file"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              JSON file
            </label>
            <input
              id="digital-assistant-schema-file"
              type="file"
              accept=".json,application/json"
              onChange={(event) => {
                void readFile(event);
              }}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200 dark:text-gray-400 dark:file:bg-gray-800 dark:file:text-gray-200 dark:hover:file:bg-gray-700"
              data-testid="ai-digital-assistants-schema-import-file"
            />
          </div>
          <Textarea
            id="digital-assistant-schema-text"
            testId="ai-digital-assistants-schema-import-text"
            label="Schema JSON"
            code
            rows={12}
            value={rawText}
            onChange={(value) => {
              setRawText(value);
              setInputError(null);
            }}
            placeholder='{"type": "object", "properties": {}}'
            helperText="The schema replaces the current rows only after you confirm the preview."
          />
          {inputError ? (
            <Alert variant="error">
              <p className="text-sm text-gray-800 dark:text-white/90">{inputError}</p>
            </Alert>
          ) : null}
        </div>
      ) : preview ? (
        <div className="space-y-4">
          {currentFieldCount > 0 ? (
            <Alert variant="warning">
              <p className="text-sm text-gray-800 dark:text-white/90">
                Importing replaces the {currentFieldCount}{' '}
                {currentFieldCount === 1 ? 'field' : 'fields'} currently in the table.
              </p>
            </Alert>
          ) : null}
          {preview.unmappableKeys.length > 0 ? (
            <Alert variant="warning">
              <p className="text-sm text-gray-800 dark:text-white/90">
                These properties are not in the business field catalog and are skipped:{' '}
                {preview.unmappableKeys.join(', ')}.
              </p>
            </Alert>
          ) : null}
          {preview.fields.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This schema has no importable fields. Importing clears the table.
            </p>
          ) : (
            <PanelTable<SchemaField>
              ariaLabel="Fields to import"
              testId="ai-digital-assistants-schema-import-preview"
              columns={columns}
              data={preview.fields}
              getRowId={(field) => field.id}
              maxHeight="50vh"
              emptyMessage="This schema has no importable fields."
              emptyDescription="Choose a schema that defines at least one field."
            />
          )}
        </div>
      ) : null}
    </Modal>
  );
}
